import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, Container, Chip, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import { alpha } from "@mui/material/styles";
import { getPurchaseInvoiceById } from "../services/purchaseService";
import { toast } from 'react-toastify';

function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPurchaseInvoice();
  }, [id]);

  const loadPurchaseInvoice = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoiceById(id);
      setInvoice(data.payload || data);
    } catch (error) {
      console.error('Error loading purchase invoice:', error);
      toast.error('Failed to load purchase invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
        </Paper>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <Typography variant="h6" color="error" mb={2}>
            Purchase invoice not found.
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate("/purchases")}>
            Back to Purchase Orders
          </Button>
        </Paper>
      </Container>
    );
  }

  const totalAmount = invoice.items?.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0) || 0;

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ mr: 2, borderRadius: 2, fontWeight: "bold", textTransform: "none" }}
              onClick={() => navigate("/purchases")}
            >
              Back
            </Button>
            <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
              <InventoryIcon sx={{ color: "#1976d2" }} fontSize="large" />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="primary">
              Purchase Invoice Details
            </Typography>
            <Chip
              label={invoice.invoiceNumber}
              color="primary"
              sx={{ ml: 2, fontWeight: 'bold', fontSize: '1em', borderRadius: '8px' }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/purchases/edit/${invoice.id}`)}
              sx={{ borderRadius: 2, fontWeight: "bold", textTransform: "none" }}
            >
              Edit
            </Button>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Supplier: <span style={{ fontWeight: 400 }}>{invoice.supplierName}</span>
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              Date: <span style={{ fontWeight: 400 }}>{invoice.purchaseDate ? new Date(invoice.purchaseDate).toLocaleDateString() : 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              Items Count: <Chip label={invoice.items?.length || 0} color="info" size="small" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              Total Amount: <span style={{ fontWeight: 400, color: '#1976d2' }}>€{totalAmount.toFixed(2)}</span>
            </Typography>
          </Box>

          <Typography variant="h6" fontWeight="bold" mb={2}>
            Items
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha("#1976d2", 0.05) }}>
                <TableCell sx={{ fontWeight: "bold" }}>Product</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">Quantity</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">Unit Price</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.items && invoice.items.length > 0 ? (
                invoice.items.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>{item.sku || 'N/A'}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">€{item.unitPrice}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      €{(item.quantity * item.unitPrice).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">No items found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </Box>
  );
}

export default PurchaseOrderDetailPage;